import React, { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  Briefcase,
  Building2,
  Users,
  Package,
  FileText,
  PlusSquare,
  ListTodo,
  FolderOpen,
  CreditCard,
  Plug,
  FileCheck,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Activity,
  UserCheck,
  MessageSquare,
  UploadCloud,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const OperatorLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isGlobalAdmin } = useAuth();
  const { activeTenant, tenants, setActiveTenant } = useTenant();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const basePath = isGlobalAdmin() ? '/global-admin' : '/operator';
  const activeTenantLabel = tenants.find((tenant) => tenant.id === activeTenant)?.name || activeTenant;

  const navItems = [
    { path: `${basePath}/dashboard`, icon: LayoutDashboard, label: t('dashboard') },
    { path: `${basePath}/workspace`, icon: Briefcase, label: 'Workspace' },
    { path: `${basePath}/tenants`, icon: Building2, label: t('tenants') },
    ...(isGlobalAdmin() ? [{ path: `${basePath}/users`, icon: Users, label: t('users') }] : []),
    ...(isGlobalAdmin()
      ? [
          { path: `${basePath}/assignments`, icon: UserCheck, label: 'Asignaciones' },
          { path: `${basePath}/activity`, icon: Activity, label: 'Actividad' },
        ]
      : []),
    ...(isGlobalAdmin() ? [{ path: `${basePath}/packages`, icon: Package, label: t('packages') }] : []),
    ...(isGlobalAdmin() ? [{ path: `${basePath}/templates`, icon: FileText, label: 'Plantillas' }] : []),
    { path: `${basePath}/addons`, icon: PlusSquare, label: t('addons') },
    { path: `${basePath}/tasks`, icon: ListTodo, label: t('tasks') },
    { path: `${basePath}/documents`, icon: FolderOpen, label: t('documents') },
    { path: `${basePath}/holded-exports`, icon: UploadCloud, label: 'Exportaciones Holded' },
    { path: `${basePath}/billing`, icon: CreditCard, label: t('billing') },
    { path: `${basePath}/chat`, icon: MessageSquare, label: 'Chat' },
    ...(isGlobalAdmin() ? [{ path: `${basePath}/integrations`, icon: Plug, label: t('integrations') }] : []),
    { path: `${basePath}/audit-logs`, icon: FileCheck, label: t('auditLogs') },
  ];

  const handleViewAsClient = () => {
    navigate('/client/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-card border-r border-border transition-all duration-300 flex flex-col`}
        data-testid="operator-sidebar"
      >
        {/* Logo/Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          {sidebarOpen && (
            <h1 className="text-xl font-heading font-bold text-primary">Zentro</h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Tenant Switcher */}
        {sidebarOpen && tenants.length > 0 && (
          <div className="p-4 border-b border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  data-testid="tenant-switcher"
                >
                  <span className="truncate">{activeTenantLabel || 'Seleccionar'}</span>
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {tenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.id}
                    onClick={() => setActiveTenant(tenant.id)}
                    data-testid={`tenant-option-${tenant.id}`}
                  >
                    {tenant.name || tenant.id}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all hover:scale-105 ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                    : 'hover:bg-accent'
                }`}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-border space-y-2">
          {sidebarOpen && (
            <Button
              variant="outline"
              className="w-full justify-center"
              onClick={handleViewAsClient}
              data-testid="view-as-client"
            >
              Ver como cliente
            </Button>
          )}
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full p-2 hover:bg-accent rounded-lg transition-colors flex justify-center"
              data-testid="logout-button-collapsed"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default OperatorLayout;

